import type { ChipFilterValue } from '@openx/types/chipFilters.ts';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import { type ChangeEvent, type Dispatch, type SetStateAction, useCallback } from 'react';
import type { TransferListProps } from '../TransferList.tsx';
import type { TransferListBaseItem, TransferListItemsMap } from '../types.ts';

type UseTransferListSelectActionsArgs<
  TItem extends TransferListBaseItem,
  TFilters extends Record<string, ChipFilterValue>,
> = {
  isItemBlocked?: TransferListProps<TItem, TFilters>['leftList']['isItemBlocked'];
  leftItems: TransferListItemsMap<TItem>;
  rightItems: TransferListItemsMap<TItem>;
  setLeftSelectedItems: Dispatch<SetStateAction<TransferListItemsMap<TItem>>>;
  setRightSelectedItems: Dispatch<SetStateAction<TransferListItemsMap<TItem>>>;
};

export const useTransferListSelectActions = <
  TItem extends TransferListBaseItem,
  TFilters extends Record<string, ChipFilterValue>,
>({
  isItemBlocked,
  leftItems,
  rightItems,
  setLeftSelectedItems,
  setRightSelectedItems,
}: UseTransferListSelectActionsArgs<TItem, TFilters>) => {
  const handleLeftSelectAll = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const emptyItems = {};
      const itemsToSelect = omitBy(leftItems, item => isItemBlocked?.(item));

      setLeftSelectedItems(event.target.checked ? itemsToSelect : emptyItems);
    },
    [isItemBlocked, leftItems, setLeftSelectedItems]
  );

  const handleLeftSelectItem = useCallback(
    (item: TItem, isSelected: boolean) => {
      setLeftSelectedItems(prev => (isSelected ? { ...prev, [item.id]: item } : omit(prev, item.id)));
    },
    [setLeftSelectedItems]
  );

  const handleRightSelectAll = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setRightSelectedItems(event.target.checked ? rightItems : {});
    },
    [rightItems, setRightSelectedItems]
  );

  const handleRightSelectItem = useCallback(
    (item: TItem, isSelected: boolean) => {
      setRightSelectedItems(prev => (isSelected ? { ...prev, [item.id]: item } : omit(prev, item.id)));
    },
    [setRightSelectedItems]
  );

  return {
    handleLeftSelectAll,
    handleLeftSelectItem,
    handleRightSelectAll,
    handleRightSelectItem,
  };
};
